import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import Consultation from "@/components/sections/Consultation";
import {
  CLINIC_REF,
  SITE_URL,
  buildBreadcrumbList,
  Crumb,
} from "@/components/schemaConstants";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Link } from "@/lib/router-compat";
import {
  CheckCircle2,
  ShieldCheck,
  Heart,
  Sparkles,
  Phone,
  ArrowRight,
  Zap,
  Brain,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  RelatedPages,
  allTreatmentsLinks,
  allServiceLinks,
  allDecisionLinks,
} from "@/components/RelatedPages";
import ClinicalReviewNote from "./ClinicalReviewNote";

export interface ConditionLink {
  title: string;
  href: string;
  active?: boolean;
}

export interface ConditionSchemaInput {
  seoTitle: string;
  seoDescription: string;
  canonical: string;
  breadcrumbs?: Crumb[];
  extraSchema?: object | object[];
}

/**
 * Build the page-level JSON-LD graphs for a condition treatment page.
 *
 * Single source of truth — consumed by each condition route's `head().scripts`
 * (server-rendered in <head>) so the MedicalWebPage + BreadcrumbList (+ any
 * extra schema) ships in the document head rather than in the body.
 */
export function buildConditionSchema(input: ConditionSchemaInput): object[] {
  const { seoTitle, seoDescription, canonical, breadcrumbs, extraSchema } =
    input;
  const canonicalPath = canonical.replace(/\/+$/, "") || "/";
  const url = `${SITE_URL.replace(/\/$/, "")}${canonicalPath.startsWith("/") ? canonicalPath : `/${canonicalPath}`}`;
  const blocks: object[] = [
    {
      "@context": "https://schema.org",
      "@type": "MedicalWebPage",
      name: seoTitle.split("|")[0].trim(),
      description: seoDescription,
      url,
      provider: CLINIC_REF,
    },
  ];
  if (breadcrumbs && breadcrumbs.length > 0) {
    blocks.push(buildBreadcrumbList(breadcrumbs, canonicalPath));
  }
  if (extraSchema) {
    const extra = Array.isArray(extraSchema) ? extraSchema : [extraSchema];
    blocks.push(...extra);
  }
  return blocks;
}

export const conditionNavLinks: ConditionLink[] = [
  { title: "Anxiety", href: "/anxiety-treatment-coral-springs-fl" },
  { title: "Bipolar Disorder", href: "/bipolar-disorder-coral-springs-fl" },
  { title: "Chronic Pain", href: "/chronic-pain-coral-springs-fl" },
  { title: "Depression", href: "/depression-treatment-coral-springs-fl" },
  { title: "CRPS", href: "/crps-treatment-coral-springs-fl" },
  { title: "Fibromyalgia", href: "/fibromyalgia-treatment-coral-springs-fl" },
  { title: "Migraines", href: "/migraines-treatment-coral-springs-fl" },
  {
    title: "Neuropathic Pain",
    href: "/neuropathic-pain-treatment-coral-springs-fl",
  },
  { title: "OCD", href: "/ocd-treatment-coral-springs-fl" },
  { title: "PTSD", href: "/ptsd-treatment-coral-springs-fl" },
  {
    title: "Suicidal Ideation",
    href: "/suicidal-ideation-treatment-coral-springs-fl",
  },
];

interface ConditionPageProps {
  badge?: string;
  title: string;
  subtitle: string;
  introHeading: string;
  introText: string[];
  whatIsTitle?: string;
  whatIsText?: string[];
  howItHelpsTitle?: string;
  howItHelpsText?: string[];
  treatmentExperienceTitle?: string;
  treatmentExperienceText?: string[];
  benefitsTitle?: string;
  benefits?: Array<{ title: string; desc: string }>;
  extraSections?: Array<{ title: string; content: string[] }>;
  imageUrl?: string;
  imageAlt?: string;
  activeConditionHref: string;
  seoTitle: string;
  seoDescription: string;
  canonical: string;
  keywords?: string;
  /** E-E-A-T: named medical reviewer (e.g. "Daniel Pietras, MD"). */
  reviewedBy?: string;
  /** E-E-A-T: ISO 8601 date of last clinical review. */
  lastUpdated?: string;
  breadcrumbs?: Crumb[];
  extraSchema?: object | object[];
}

export const ConditionPageLayout: React.FC<ConditionPageProps> = ({
  badge = "IV Ketamine Therapy",
  title,
  subtitle,
  introHeading,
  introText,
  whatIsTitle,
  whatIsText,
  howItHelpsTitle,
  howItHelpsText,
  treatmentExperienceTitle = "What is it Like During a Ketamine Treatment?",
  treatmentExperienceText,
  benefitsTitle = "Key Benefits of Ketamine Treatment",
  benefits,
  extraSections,
  imageUrl,
  imageAlt,
  activeConditionHref,
  seoTitle,
  seoDescription,
  canonical,
  keywords,
  reviewedBy,
  lastUpdated,
  breadcrumbs,
  extraSchema,
}) => {
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="pt-16 sm:pt-20">
        {/* Hero Banner — clean navy background */}
        <section className="relative text-primary-foreground py-16 sm:py-24 overflow-hidden bg-primary">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/95 via-primary/90 to-primary-deep/90" />
          <div className="container-px relative mx-auto max-w-5xl px-4">
            <div className="max-w-xl text-left space-y-5">
              <span className="inline-block rounded-full bg-accent/90 backdrop-blur-sm px-6 py-2 text-sm sm:text-base font-bold text-white shadow-lg uppercase tracking-wider">
                {badge}
              </span>
              <h1 className="font-headings text-3xl sm:text-5xl font-extrabold text-white leading-tight drop-shadow-md">
                {title}
              </h1>
              <p className="text-base sm:text-lg text-primary-foreground/90 leading-relaxed drop-shadow-md">
                {subtitle}
              </p>
              <div className="flex flex-col sm:flex-row items-start justify-start gap-4 pt-3">
                <a
                  href="/#consultation"
                  className="inline-flex items-center justify-center rounded-xl bg-gradient-accent px-8 py-6 text-base font-bold text-accent-foreground shadow-glow hover:opacity-90 transition-opacity"
                >
                  Book an Appointment
                </a>
                <a
                  href="tel:9548279793"
                  className="inline-flex items-center justify-center rounded-xl border-2 border-white/60 bg-white/10 backdrop-blur-sm px-8 py-6 text-base font-bold text-white hover:bg-white/20 hover:border-white/80 transition-all"
                >
                  <Phone className="h-5 w-5 mr-2" />
                  Call 954-827-9793
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Breadcrumbs */}
        {breadcrumbs && breadcrumbs.length > 0 && (
          <div className="bg-background py-3">
            <div className="container-px mx-auto max-w-6xl px-4">
              <Breadcrumbs crumbs={breadcrumbs} />
            </div>
          </div>
        )}

        {/* Content + Sidebar Layout */}
        <section className="py-12 sm:py-16 bg-background">
          <div className="container-px mx-auto max-w-6xl grid grid-cols-1 lg:grid-cols-3 gap-10">
            {/* Main Article Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* E-E-A-T review note */}
              {(reviewedBy || lastUpdated) && (
                <ClinicalReviewNote
                  reviewedBy={reviewedBy}
                  lastUpdated={lastUpdated}
                />
              )}
              {/* Intro Section */}
              <div className="space-y-4">
                <h2 className="font-headings text-2xl sm:text-3xl font-extrabold text-primary">
                  {introHeading}
                </h2>
                {introText.map((p, i) => (
                  <p
                    key={i}
                    className="text-foreground/85 text-base sm:text-lg leading-relaxed"
                  >
                    {p}
                  </p>
                ))}
              </div>

              {/* What Is Section */}
              {whatIsTitle && whatIsText && (
                <div className="rounded-2xl bg-card border border-border p-6 sm:p-8 shadow-soft space-y-4">
                  <div className="flex items-center gap-3 text-primary">
                    <Brain className="h-6 w-6 text-accent shrink-0" />
                    <h3 className="font-headings text-xl font-bold">
                      {whatIsTitle}
                    </h3>
                  </div>
                  {whatIsText.map((p, i) => (
                    <p
                      key={i}
                      className="text-foreground/85 text-sm sm:text-base leading-relaxed"
                    >
                      {p}
                    </p>
                  ))}
                </div>
              )}

              {/* How It Helps Section */}
              {howItHelpsTitle && howItHelpsText && (
                <div className="space-y-4">
                  <h3 className="font-headings text-xl sm:text-2xl font-bold text-primary flex items-center gap-2">
                    <Zap className="h-6 w-6 text-accent shrink-0" />
                    {howItHelpsTitle}
                  </h3>
                  {howItHelpsText.map((p, i) => (
                    <p
                      key={i}
                      className="text-foreground/85 text-base leading-relaxed"
                    >
                      {p}
                    </p>
                  ))}
                </div>
              )}

              {/* Treatment Experience */}
              {treatmentExperienceText && (
                <div className="rounded-2xl bg-light-blue/30 border border-light-blue p-6 sm:p-8 space-y-4">
                  <h3 className="font-headings text-xl font-bold text-primary">
                    {treatmentExperienceTitle}
                  </h3>
                  {treatmentExperienceText.map((p, i) => (
                    <p
                      key={i}
                      className="text-foreground/85 text-base leading-relaxed"
                    >
                      {p}
                    </p>
                  ))}
                </div>
              )}

              {/* Image Banner if available */}
              {imageUrl && (
                <div className="rounded-2xl overflow-hidden border border-border shadow-soft">
                  <img
                    src={imageUrl}
                    alt={imageAlt || title}
                    className="w-full h-auto object-cover max-h-96"
                    loading="lazy"
                    width={1200}
                    height={675}
                  />
                </div>
              )}

              {/* Benefits */}
              {benefits && benefits.length > 0 && (
                <div className="space-y-6">
                  <h3 className="font-headings text-xl sm:text-2xl font-bold text-primary">
                    {benefitsTitle}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {benefits.map((b, i) => (
                      <div
                        key={i}
                        className="rounded-xl bg-card border border-border p-5 shadow-soft space-y-2"
                      >
                        <div className="flex items-center gap-2 text-accent font-bold text-base">
                          <CheckCircle2 className="h-5 w-5 shrink-0" />
                          <span>{b.title}</span>
                        </div>
                        <p className="text-xs sm:text-sm text-foreground/80 leading-relaxed">
                          {b.desc}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Extra Custom Sections */}
              {extraSections &&
                extraSections.map((sec, i) => (
                  <div key={i} className="space-y-3 pt-2">
                    <h3 className="font-headings text-xl font-bold text-primary">
                      {sec.title}
                    </h3>
                    {sec.content.map((p, j) => (
                      <p
                        key={j}
                        className="text-foreground/85 text-base leading-relaxed"
                      >
                        {p}
                      </p>
                    ))}
                  </div>
                ))}

              {/* Contact Callout */}
              <div className="rounded-2xl bg-gradient-navy text-white p-8 text-center space-y-4 shadow-premium">
                <h3 className="font-headings text-2xl font-bold">
                  Ready to Start Your Healing Journey?
                </h3>
                <p className="text-primary-foreground/80 max-w-xl mx-auto text-sm sm:text-base">
                  Contact Neuro Wellness TMS Centers of America in Coral
                  Springs, FL today to schedule a confidential consultation.
                </p>
                <div className="pt-2 flex justify-center gap-4">
                  <Button
                    asChild
                    size="lg"
                    className="bg-gradient-accent text-accent-foreground shadow-glow hover:opacity-90"
                  >
                    <a href="tel:9548279793">
                      <Phone className="h-4 w-4 mr-2" />
                      Call (954) 827-9793
                    </a>
                  </Button>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-8">
              {/* Quick Consult Card */}
              <div className="rounded-2xl border border-border bg-card p-6 shadow-soft space-y-4 sticky top-24">
                <h3 className="font-headings text-xl font-bold text-primary text-center">
                  Request an Appointment
                </h3>
                <p className="text-xs text-muted-foreground text-center">
                  Fill out the form below or call us at (954) 827-9793.
                </p>
                <a
                  href="/#consultation"
                  className="block w-full py-3 px-4 rounded-xl bg-gradient-accent text-accent-foreground text-center font-bold text-sm shadow-glow hover:opacity-90 transition-all"
                >
                  Book Free Consultation
                </a>

                <hr className="border-border" />

                {/* Conditions Treated Navigation Box */}
                <div className="space-y-3">
                  <h4 className="font-headings text-sm font-bold uppercase tracking-wider text-muted-foreground">
                    Conditions Treated with Ketamine
                  </h4>
                  <div className="flex flex-col gap-1.5">
                    {conditionNavLinks.map((item, idx) => {
                      const isActive = item.href === activeConditionHref;
                      return (
                        <Link
                          key={idx}
                          to={item.href}
                          className={`px-3 py-2 rounded-lg text-sm font-semibold transition-all flex items-center justify-between ${
                            isActive
                              ? "bg-accent text-white shadow-sm"
                              : "bg-muted/40 text-foreground/85 hover:bg-muted hover:text-primary"
                          }`}
                        >
                          <span>{item.title}</span>
                          <ArrowRight
                            className={`h-3.5 w-3.5 ${isActive ? "text-white" : "opacity-40"}`}
                          />
                        </Link>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <RelatedPages
          heading="Explore Related Treatments & Resources"
          links={[
            ...allTreatmentsLinks,
            ...allServiceLinks,
            ...allDecisionLinks,
          ]}
        />

        <Consultation />
      </main>
      <Footer />
    </div>
  );
};
